import cn from 'clsx'

// Components
import { IconCheckmarkFilled, Button } from '@tracklib/kit'
import { ShadowBox } from 'kit'

// Hooks
import { useProfile } from 'modules/profile/hooks'

// Types
import { RevenueStream, UserSong } from 'types'

// Utils
import { SOURCE_OPTIONS } from 'components/usersong/accounting/utils'
import { priceString } from 'components/base/price'

import styles from './revenuereport.css'

type RowProps = {
  source: string
  amount: string
  share: string
  head?: boolean
  summary?: boolean
}

const Row = ({ source, amount, share, head, summary }: RowProps): JSX.Element => (
  <li className={cn(styles.row, head && styles.head, summary && styles.rowSummary)}>
    <strong>{source}</strong>
    <div className={styles.right}>
      <p className={styles.amount}>{amount}</p>
      <p className={styles.share}>{share}</p>
    </div>
  </li>
)

type RevenueReportProps = {
  id: number
  reported: boolean
  reportingOpen: string
  reportingClose: string
  revenueStreams: Array<RevenueStream>
  totalAmount: number
  userSong: UserSong
  showReportButton?: boolean
}

const RevenueReport = ({
  id,
  reported,
  revenueStreams,
  totalAmount,
  reportingOpen,
  reportingClose,
  userSong: { artist_name, song_title },
  showReportButton = true,
}: RevenueReportProps): JSX.Element => {
  const profile = useProfile()
  const revenueReported = reported || revenueStreams.length

  const ReportButton = ({ mobile }: { mobile?: boolean }) => {
    const nowDate = new Date()
    const reportingIsOpen = new Date(reportingOpen) <= nowDate && nowDate <= new Date(reportingClose)

    if (reportingIsOpen && showReportButton) {
      return (
        <Button
          to="revenuereport"
          query={{ revenueReportId: id }}
          modal
          className={mobile ? styles.reportButtonMobile : styles.reportButton}
        >
          Report revenue
        </Button>
      )
    }
    return null
  }

  const recoupUsed =
    revenueReported && revenueStreams.length > 0 ? revenueStreams.reduce((acc, s) => acc + s.recouped_amount_converted, 0) : 0
  return (
    <ShadowBox className={styles.wrapper}>
      <header className={styles.header}>
        <h3 className={styles.title}>
          <IconCheckmarkFilled checked={reported} /> {artist_name} - {song_title}
        </h3>
        <ReportButton />
      </header>

      {revenueReported ? (
        <>
          {revenueStreams.length > 0 ? (
            <>
              <Row head source="Source" amount="Revenue" share={`Royalty in ${profile.currency}`} />
              {revenueStreams
                .sort((a, b) => {
                  if (a.is_locked === b.is_locked) {
                    return 0
                  }
                  return a.is_locked ? -1 : 1
                })
                .sort((a, b) => {
                  if (a.already_paid === b.already_paid) {
                    return 0
                  }
                  return a.already_paid ? -1 : 1
                })
                .map(({ id: streamId, source, amount, currency, already_paid, royalty_amount_converted }) => (
                  <Row
                    key={streamId}
                    source={`${
                      SOURCE_OPTIONS[source as keyof typeof SOURCE_OPTIONS]
                        ? SOURCE_OPTIONS[source as keyof typeof SOURCE_OPTIONS].label
                        : source
                    }${already_paid ? ' (Paid)' : ''}`}
                    amount={`${currency} ${amount}`}
                    share={priceString(royalty_amount_converted, profile.currency)}
                  />
                ))}
              {revenueReported && (
                <footer className={styles.footer}>
                  {recoupUsed > 0 && <Row summary source="" amount="Recouped:" share={`-${priceString(recoupUsed, profile.currency)}`} />}
                  <Row summary source="" amount="TOTAL:" share={priceString(totalAmount - recoupUsed, profile.currency)} />
                  <ReportButton mobile />
                </footer>
              )}
            </>
          ) : (
            <footer className={styles.messageFooter}>
              You have reported 0 revenue for this song <ReportButton mobile />
            </footer>
          )}
        </>
      ) : (
        <>
          {showReportButton && (
            <footer className={styles.mobileMessageFooter}>
              <ReportButton mobile />
            </footer>
          )}
        </>
      )}
    </ShadowBox>
  )
}

export default RevenueReport
